/**
 * Authentication store using Zustand
 * Manages user authentication state and operations
 * FIXED: Added error state management
 */

import { create } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';
import { setAuthToken } from '@services/api';
import toast from 'react-hot-toast';

const useAuthStore = create(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        // Auth state
        user: null,
        isAuthenticated: false,
        isLoading: false,
        sessionId: null,
        
        // Error state - NEW
        authError: null,
        
        // User preferences
        preferences: {
          theme: 'light',
          language: 'en',
          autoplay: true,
          notifications: true,
          wakeWordEnabled: true, // Phase 5: Wake word detection enabled by default
        },
        
        // Actions
        setUser: (user) => {
          set({ 
            user, 
            isAuthenticated: !!user,
            isLoading: false,
            authError: null // Clear error on successful auth
          });
        },
        
        setLoading: (isLoading) => {
          set({ isLoading });
        },
        
        setSession: (sessionId) => {
          set({ sessionId });
        },
        
        // NEW: Clear error action
        clearError: () => {
          set({ authError: null });
        },
        
        // NEW: Set error action
        setError: (error) => {
          set({ authError: error });
        },
        
        login: async (credentials) => {
          set({ isLoading: true, authError: null }); // Clear previous errors
          
          try {
            const response = await import('@services/authApi').then(module => 
              module.authApi.login(credentials)
            );
            
            if (response.success) {
              const { user, token, sessionId } = response.data;
              
              setAuthToken(token);
              
              set({
                user,
                isAuthenticated: true,
                sessionId,
                isLoading: false,
                authError: null // Clear any errors
              });
              
              toast.success(`Welcome, ${user.userName || user.firstName || user.name}!`);
              return { success: true, data: response.data };
            } else {
              const errorMessage = response.message || 'Login failed';
              set({ 
                isLoading: false, 
                authError: errorMessage // Store error in state
              });
              return { success: false, error: errorMessage };
            }
          } catch (error) {
            const message = error.message || 'Login failed. Please try again.';
            set({ 
              isLoading: false,
              authError: message // Store error in state
            });
            return { success: false, error: message };
          }
        },
        
        googleLogin: async (credential, userType = 'student') => {
          set({ isLoading: true, authError: null }); // Clear previous errors
          
          try {
            const response = await import('@services/authApi').then(module => 
              module.authApi.googleLogin(credential, userType)
            );
            if (response.success) {
              const { user, token, sessionId } = response.data;
              
              setAuthToken(token);
              
              set({
                user,
                isAuthenticated: true,
                sessionId,
                isLoading: false,
                authError: null // Clear any errors
              });
              
              toast.success(`Welcome, ${user.firstName || user.name}!`);
              return { success: true, data: response.data };
            } else {
              // Login failed - store error in state
              const message = response.message || response.error || 'Google login failed';
              set({ 
                isLoading: false,
                authError: message // Store error in state - will persist
              });
              
              return { success: false, error: message };
            }
          } catch (error) {
            const message = error.response?.data?.message || error.message || 'Google login failed. Please try again.';
            set({ 
              isLoading: false,
              authError: message // Store error in state - will persist
            });
            
            return { success: false, error: message };
          }
        },
        
        googleSignup: async (credential, signupData = {}) => {
          set({ isLoading: true, authError: null });
          
          try {
            const response = await import('@services/authApi').then(module => 
              module.authApi.googleSignup(credential, signupData)
            );
            
            if (response.success) {
              localStorage.setItem('googleSignupData', JSON.stringify(response.data));
              set({ isLoading: false, authError: null });
              return { success: true, data: response.data };
            } else {
              const errorMessage = response.message || 'Google signup failed';
              set({ 
                isLoading: false, 
                authError: errorMessage 
              });
              toast.error(errorMessage);
              return { success: false, error: errorMessage };
            }
          } catch (error) {
            const message = error.message || 'Google signup failed. Please try again.';
            set({ 
              isLoading: false,
              authError: message
            });
            toast.error(message);
            return { success: false, error: message };
          }
        },
        
        logout: async () => {
          set({ isLoading: true });
          
          try {
            await import('@services/authApi').then(module => 
              module.authApi.logout(get().sessionId)
            );
          } catch (error) {
            console.error('Logout API error:', error);
          }
          
          setAuthToken(null);
          set({
            user: null,
            isAuthenticated: false,
            sessionId: null,
            isLoading: false,
            authError: null // Clear errors on logout
          });
          
          toast.success('Logged out successfully');
        },
        
        updateProfile: async (updates) => {
          const currentUser = get().user;
          if (!currentUser) return { success: false, error: 'Not authenticated' };
          
          try {
            const response = await import('@services/authApi').then(module => 
              module.authApi.updateProfile(updates)
            );
            
            if (response.success) {
              set({
                user: { ...currentUser, ...response.data },
                authError: null
              });
              
              toast.success('Profile updated successfully');
              return { success: true, data: response.data };
            } else {
              const errorMessage = response.message || 'Profile update failed';
              set({ authError: errorMessage });
              throw new Error(errorMessage);
            }
          } catch (error) {
            const message = error.message || 'Failed to update profile';
            set({ authError: message });
            toast.error(message);
            return { success: false, error: message };
          }
        },
        
        updatePreferences: (newPreferences) => {
          set({
            preferences: { ...get().preferences, ...newPreferences }
          });
        },
        
        // Initialize auth state (check for existing session)
        initialize: async () => {
          const token = localStorage.getItem('authToken');
          const currentState = get();
          // Don't clear errors during initialization
          // This preserves login errors across remounts
          
          if (!token) {
            set({ 
              user: null,
              isAuthenticated: false,
              sessionId: null,
              isLoading: false
              // NOTE: We don't clear authError here
            });
            return;
          }
          
          if (currentState.user && currentState.isAuthenticated) {
            set({ isLoading: true });
            
            try {
              const response = await import('@services/authApi').then(module => 
                module.authApi.verifyToken()
              );
              
              if (response.success) {
                set({
                  user: response.data.user || currentState.user,
                  isAuthenticated: true,
                  sessionId: response.data.sessionId || currentState.sessionId,
                  isLoading: false,
                  authError: null // Clear error only on successful verification
                });
              } else {
                setAuthToken(null);
                set({
                  user: null,
                  isAuthenticated: false,
                  sessionId: null,
                  isLoading: false
                  // Don't clear authError - preserve it
                });
              }
            } catch (error) {
              console.error('❌ Token verification failed:', error);
              setAuthToken(null);
              set({
                user: null,
                isAuthenticated: false,
                sessionId: null,
                isLoading: false
                // Don't clear authError - preserve it
              });
            }
          } else {
            setAuthToken(null);
            set({
              user: null,
              isAuthenticated: false,
              sessionId: null,
              isLoading: false
              // Don't clear authError - preserve it
            });
          }
        },
        
        // Helper getters
        getUser: () => get().user,
        getSessionId: () => get().sessionId,
        isStudent: () => get().user?.userType === 'student',
        isParent: () => get().user?.userType === 'parent',
        getUserName: () => {
          const user = get().user;
          return user?.firstName || user?.name || user?.email || 'User';
        },
        
        // Clear all auth state (for debugging)
        clearAuth: () => {
          setAuthToken(null);
          set({
            user: null,
            isAuthenticated: false,
            sessionId: null,
            isLoading: false,
            authError: null
          });
        },
      }),
      {
        name: 'auth-store',
        partialize: (state) => ({
          user: state.user,
          isAuthenticated: state.isAuthenticated,
          sessionId: state.sessionId,
          preferences: state.preferences,
          authError: state.authError // Persist error too
        }),
        onRehydrateStorage: () => (state) => {
          if (state) {
            state.initialize();
          }
        },
      }
    )
  )
);

// Subscribe to auth changes for side effects
useAuthStore.subscribe(
  (state) => state.isAuthenticated,
  (isAuthenticated, previousIsAuthenticated) => {
    if (isAuthenticated && !previousIsAuthenticated) {
    } else if (!isAuthenticated && previousIsAuthenticated) {
    }
  }
);

export { useAuthStore };
